import React, {useState} from 'react';
import { ChevronUp, ChevronDown, Calendar as CalendarIcon, Factory, ShoppingCart, Truck } from 'lucide-react';
import { DeliveryOption, ProductionDate, Shipment } from '../models/DeliveryOptionDetailsModel';
import { getCarrierIcons } from './CarrierIcons';
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";

interface CalendarSectionProps {
  shipment: Shipment;  
  prodCalendar: Record<string, ProductionDate[]>
  orderDate: string | null
}

const CalendarSection: React.FC<CalendarSectionProps> = ({
  shipment, prodCalendar, orderDate
}) => {
  const [openStates, setOpenStates] = useState<Record<string, boolean>>({});
  const toggle = (id: string) => setOpenStates(prev => ({ ...prev, [id]: !prev[id] }));
  const firstTag = shipment.Products[0]?.Tag;
  const shipDateStr = shipment.ShipDate;                                
  const calendarData = prodCalendar[firstTag] || [];    
  const deliveryDates = shipment.DeliveryOptions?.map((o: any) => o.EstimatedDeliveryDate).filter(Boolean);
  const slowestDeliveryDateStr = deliveryDates?.length ? deliveryDates.sort().reverse()[0] : null;         

  return (
    <div className="rounded-lg border-2 border-teal-200 overflow-hidden bg-white shadow-sm">
                        <div 
                          style={{ 
                            display: 'flex', 
                            flexDirection: 'row', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '10px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                          className="hover:bg-slate-50"
                          onClick={() => toggle('cal')}
                        >
                          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                            <CalendarIcon size={12} className="text-teal-600 shrink-0" />
                            <span className="text-[11px] font-bold text-teal-950 uppercase shrink-0">Calendar</span>
                            <span className="text-[10px] font-mono font-black text-teal-600 opacity-70">
                              [Production Schedule]
                            </span>
                          </div>
                          {openStates['cal'] ? <ChevronUp size={14} className="text-teal-900 shrink-0" /> : <ChevronDown size={14} className="text-teal-900 shrink-0" />}
                        </div>
                        {openStates['cal'] && (
                          <div className="px-4 pb-4 pt-3 border-t border-teal-100 bg-teal-50/30 text-black">
                            <div className="max-w-[260px] mx-auto border-2 border-black rounded-md overflow-hidden bg-white shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] fc-mini-compact">
                              <FullCalendar
                                plugins={[dayGridPlugin]}
                                initialView="dayGridMonth"
                                height="auto"
                                headerToolbar={{ left: "prev", center: "title", right: "next" }}
                                dayCellClassNames={(arg) => {
                                  const dateStr = arg.date.toLocaleDateString('en-CA'); 
                                  const dayData = calendarData.find(d => d.Date === dateStr);
                                  return dayData?.IsProductionDate ? 'prod-work' : 'prod-closed';
                                }}
                                dayCellContent={(arg) => {
                                  const dateStr = arg.date.toLocaleDateString('en-CA');
                                  const dayData = calendarData.find(d => d.Date === dateStr);
                                  const orderDateStr = orderDate as string | null;
                                  const isOrderDate = orderDateStr === dateStr;
                                  const isManufacturingDay = orderDateStr && dateStr > orderDateStr && (shipDateStr ? dateStr <= (shipDateStr as string) : false) && dayData?.IsProductionDate;
                                  const isTransportDay = shipDateStr && slowestDeliveryDateStr ? (dateStr >= (shipDateStr as string) && dateStr <= (slowestDeliveryDateStr as string)) : false;
                                  const deliveryOnThisDate = shipment.DeliveryOptions?.filter((o: any) => o.EstimatedDeliveryDate === dateStr) || [];
                                  
                                  return (
                                    <div className="w-full h-full flex flex-col items-center justify-start p-0.5 relative min-h-[70px] overflow-hidden text-black">
                                      <span className="text-[9px] font-black z-10 mb-0.5">{arg.dayNumberText}</span>
                                      <div className="absolute inset-0 top-4 flex flex-col items-center gap-0.5 pointer-events-none">
                                        {isOrderDate && (
                                          <div title="Order Date">
                                            <ShoppingCart size={22} className="text-[#1e3a8a] stroke-[2.5] opacity-60" />
                                          </div>
                                        )}
                                        {isManufacturingDay && !isOrderDate && (
                                          <div title="Manufacturing">
                                            <Factory size={22} className="text-[#064e3b] stroke-[2] opacity-60" />
                                          </div>
                                        )}
                                        {isTransportDay && !isOrderDate && deliveryOnThisDate.length === 0 && (
                                          <div title="Transport">
                                            <Truck size={22} className="text-[#0f172a] stroke-[2] opacity-60" />
                                          </div>
                                        )}
                                        {deliveryOnThisDate.length > 0 && (
                                          <div className={deliveryOnThisDate.length > 2 ? "grid grid-cols-2 gap-0.5" : "flex flex-col gap-0.5"}>
                                            {deliveryOnThisDate.map((opt: DeliveryOption, idx: number) => {
                                              const { methodIcon, typeIcon} = getCarrierIcons({DeliveryMethod: opt.ShippingDetails.DeliveryMethod, DeliveryType: opt.ShippingDetails.DeliveryType});
                                              return (
                                                <div key={idx} className="flex items-center gap-[1px] bg-white/90 p-[1px] border border-slate-200 rounded shadow-sm shrink-0">
                                                  <img src={methodIcon} alt="m" title={opt.ShippingDetails.DeliveryMethod} style={{ height: '18px', width: '18px' }} className="object-contain" />
                                                  <img src={typeIcon} alt="t" title={opt.ShippingDetails.DeliveryType} style={{ height: '18px', width: '18px' }} className="object-contain" />
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
    );
};

export default CalendarSection;